import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY, PREVIEW_KEY, readPreview, isAdmin } from "@/lib/api";
import { DEFAULT_THEME } from "@/lib/themes";

const AppContext = createContext(null);
const THEME_CACHE = "rep_theme";

const applyTheme = (t) => {
  document.documentElement.dataset.theme = t;
  localStorage.setItem(THEME_CACHE, t);
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [theme, setThemeState] = useState(localStorage.getItem(THEME_CACHE) || DEFAULT_THEME);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    api.get("/settings/public").then((r) => setThemeState(r.data.theme)).catch(() => {});
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(false);
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    api.get("/settings/public").then((r) => setThemeState(r.data.theme)).catch(() => {});
    return data.user;
  }, []);

  const [preview, setPreview] = useState(readPreview());
  const startPreview = useCallback((b) => {
    sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(b));
    setPreview(b);
  }, []);
  const exitPreview = useCallback(() => {
    sessionStorage.removeItem(PREVIEW_KEY);
    setPreview(null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(PREVIEW_KEY);
    setPreview(null);
    setUser(false);
  }, []);

  const saveTheme = useCallback(async (t) => {
    await api.put("/settings/theme", { theme: t });
    setThemeState(t);
  }, []);

  return (
    <AppContext.Provider value={{ user, setUser, login, logout, theme, saveTheme, preview: isAdmin(user) ? preview : null, startPreview, exitPreview }}>{children}</AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
